import React, { useEffect, useState } from "react";
import {
  CircularProgress,
  Table,
  TableBody,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
} from '@material-ui/core';
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';
import UpIcon from '@material-ui/icons/KeyboardArrowUp';
import { FormattedMessage, useIntl } from 'react-intl';
import swal from 'sweetalert';
import SortAsc from "../../assets/images/llantas/table/sort-asc.svg";
import SortDesc from "../../assets/images/llantas/table/sort-desc.svg";
import Sort from "../../assets/images/llantas/table/sort.svg";
import Security from '../../commons/security/Security.js';
import { StyledTableCellProps as StyledTableCell, StyledTooltip } from '../../commons/utils/TableStyles';
import * as Utils from '../../commons/utils/Utils';
import ConfigBusiness from '../../commons/config/ConfigBusiness.js';
import { ID_OCIOSIDAD } from '../../commons/constants/tipoMotivos.js';
import Timezone from "../../commons/timezone/Timezone.js";
import DisponibilidadActivaGridRow from './DisponibilidadActivaGridRow.jsx';
import DisponibilidadActivaGridFilters from './DisponibilidadActivaGridFilters.jsx';
import DisponibilidadGridImport from './DisponibilidadGridImport.jsx';
import DisponibilidadImportarReporte from './DisponibilidadImportarReporte.jsx';
import DisponibilidadActivaExport from './DisponibilidadActivaExport.jsx';
import UltimaActualizacion from './UltimaActualizacion.jsx';

const PAGINATION_INIT = { page: 0, count: 0, perPage: 10 };

const FILTER_QUERY_INIT = {
  dominio: "",
  modelo: "",
  tipo: "",
  base: "",
  region: "",
  subRegion: "",
  centroCostos: "",
  motivo: "",
  transportadora: "",
};

const SORT_QUERY_INIT = {
  dominio: "",
  modelo: "",
  tipo: "",
  base: "",
  region: "",
  subRegion: "",
  centroCostos: "",
  motivo: "",
  transportadora: "",
  rentadora: "",
  fechaInicio: "",
};

const DisponibilidadActivaGrid = (props) => {
  const intl = useIntl();
  const [modalImport, setModalImport] = useState(false);
  const [modalImportReport, setModalImportReport] = useState(false);
  const [importReportData, setImportReportData] = useState(null);
  const [modalExport, setModalExport] = useState(false);
  const [ultimaActualizacion, setUltimaActualizacion] = useState(null);
  const [loading, setLoading] = useState(false);
  const [pagination, setPagination] = useState({ ...PAGINATION_INIT });
  const [paginationCount, setPaginationCount] = useState(0);
  const [filterQuery, setFilterQuery] = useState({ ...FILTER_QUERY_INIT });
  const [sortQuery, setSortQuery] = useState({ ...SORT_QUERY_INIT });
  const [dropdownActive, setDropdownActive] = useState(false);
  const [moviles, setMoviles] = useState([]);
  const [comboMotivos, setComboMotivos] = useState([]);
  const [columnsEnabled, setColumnsEnabled] = useState({
    transportadora: false,
    rentadora: false,
  });

  useEffect(() => {
    const aux = { ...columnsEnabled };
    aux.transportadora = ConfigBusiness.get("moviles.transportadora.grilla") === 'true';
    aux.rentadora = ConfigBusiness.get("frontend.rentadora.grilla") === 'true';
    setColumnsEnabled(aux);
  }, []);

  useEffect(() => {
    if (Security.hasPermission("DISPONIBILIDAD_ACTIVA_LISTAR")) {
      init();
    }
  }, [pagination, sortQuery]);

  const init = () => {
    setLoading(true);
    const motivosReq = Utils.getData(`/motivos/simple-search?tipo_motivo=${ID_OCIOSIDAD}`);
    const gridReq = Utils.getData(`/disponibilidad/activa${getQuery()}`);
    Promise.all([motivosReq, gridReq])
      .then(([motivos, data]) => {
        setComboMotivos(motivos || []);
        setMoviles(data.moviles || []);
        setPaginationCount(data.pagination.count);
        setUltimaActualizacion(data.ultima_actualizacion || null);
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  };

  const getQuery = () => {
    let q = `?page=${pagination.page}&perPage=${pagination.perPage}`;
    q += `&dominio=${filterQuery.dominio}`;
    q += `&modelo=${filterQuery.modelo}`;
    q += `&tipo=${filterQuery.tipo}`;
    q += `&base=${filterQuery.base}`;
    q += `&region=${filterQuery.region}`;
    q += `&subRegion=${filterQuery.subRegion}`;
    q += `&centroCostos=${filterQuery.centroCostos}`;
    q += `&motivo=${filterQuery.motivo}`;
    q += `&transportadora=${filterQuery.transportadora}`;
    q += `&dominioSort=${sortQuery.dominio}`;
    q += `&modeloSort=${sortQuery.modelo}`;
    q += `&tipoSort=${sortQuery.tipo}`;
    q += `&baseSort=${sortQuery.base}`;
    q += `&regionSort=${sortQuery.region}`;
    q += `&subRegionSort=${sortQuery.subRegion}`;
    q += `&centroCostosSort=${sortQuery.centroCostos}`;
    q += `&motivoSort=${sortQuery.motivo}`;
    q += `&transportadoraSort=${sortQuery.transportadora}`;
    q += `&rentadoraSort=${sortQuery.rentadora}`;
    q += `&fechaInicioSort=${sortQuery.fechaInicio}`;
    return q;
  };

  const handleChangePage = (event, newPage) => {
    setPagination({ ...pagination, page: newPage });
  };

  const handleChangeRowsPerPage = (event) => {
    setPagination({ ...pagination, perPage: parseInt(event.target.value, 10), page: 0 });
  };

  const requestSort = (key) => {
    const query = { ...SORT_QUERY_INIT };
    const current = sortQuery[key];
    query[key] = !current ? "asc" : current === "desc" ? "asc" : "desc";
    setPagination(prev => ({ ...prev, page: 0 }));
    setSortQuery(query);
  };

  const handleFilterSearch = (query) => {
    setFilterQuery({ ...FILTER_QUERY_INIT, ...query });
    setPagination(prev => ({ ...prev, page: 0 }));
  };

  const handleFilterReset = () => {
    setFilterQuery({ ...FILTER_QUERY_INIT });
    setSortQuery({ ...SORT_QUERY_INIT });
    setPagination({ ...PAGINATION_INIT });
  };

  const handleModalExportOpen = () => {
    setModalExport(true);
  };

  const handleModalExportClose = () => {
    setModalExport(false);
  };

  const handleModalImportOpen = () => {
    setModalImport(true);
  };

  // Callback tipo resetModal, igual que en DisponibilidadGrid
  const handleImportCloseModal = (message, result, error) => {
    setModalImport(false);
    if (error && error.codigo === 4010) {
      swal(
        intl.formatMessage({ id: 'disponibilidadGrid.alert.sesion_expiro', defaultMessage: 'La sesión expiró' }),
        intl.formatMessage({ id: 'disponibilidadGrid.alert.ingrese_nuevamente', defaultMessage: 'Por favor, ingrese nuevamente.' }),
        "error"
      ).then(() => {
        window.location.reload();
      });
    } else if (error && (error.tipo === "filetype" || error.tipo === "error")) {
      swal(
        intl.formatMessage({
          id: 'disponibilidadImportar.errors.tipo_archivo_no_soportado',
          defaultMessage: 'Error al importar el archivo. El tipo de archivo no es soportado.',
        }),
        '',
        'error'
      );
    } else if (error && error.errores && Array.isArray(error.errores) && error.errores.length > 0) {
      setImportReportData(error.errores.map((e) => (typeof e === 'string' ? { fila: '', errores: [e] } : e)));
      setModalImportReport(true);
    } else if (message && (!error || !error.errores)) {
      swal(message, '', 'success');
    } else if (message && error) {
      swal(message, '', 'error');
    }
  };

  const handleImportReportClose = () => {
    setModalImportReport(false);
    setImportReportData(null);
  };

  const handleActualizarMotivo = (motivoId, historialId) => {
    setMoviles(prev =>
      prev.map(m => m.id === historialId
        ? {
            ...m,
            motivo_id: motivoId,
            fecha_actualizacion_motivo: Timezone.getDateForClient('now', null, 'YYYY-MM-DD HH:mm'),
            usuarioModificacionMotivo: getNombreUsuario(),
          }
        : m
      )
    );
  };

  const getNombreUsuario = () => {
    const persona = JSON.parse(localStorage.getItem("persona"));
    return persona ? `${persona.nombre} ${persona.apellido}` : '';
  };

  const sortIcon = (key) => (
    <img
      className="ml-1"
      src={sortQuery[key] === "" ? Sort : sortQuery[key] === "asc" ? SortAsc : SortDesc}
      alt="/"
      style={{ height: "12px", width: "22px" }}
    />
  );

  if (!Security.hasPermission("DISPONIBILIDAD_ACTIVA_LISTAR")) {
    return null;
  }

  const searchActive = Object.values(filterQuery).some(v => v !== "");

  return (
    <div className="card">
      <DisponibilidadActivaExport
        open={modalExport}
        handleClose={handleModalExportClose}
        queryFilters={filterQuery}
      />

      <DisponibilidadGridImport
        open={modalImport}
        handleClose={() => setModalImport(false)}
        callbackClose={handleImportCloseModal}
        handleFilterReset={handleFilterReset}
      />

      {modalImportReport && (
        <div className="modal fade show" tabIndex="-1" role="dialog" style={{ display: 'block' }}>
          <div className="modal-dialog modal-lg" role="document">
            <div className="modal-content">
              <div className={`modal-header ${modalImportReport === 'filetype' ? 'bg-danger' : 'bg-fleet'}`}>
                <h4 className="modal-title text-white" id="importReportLabelActiva">
                  {modalImportReport === 'filetype' ? (
                    <>
                      <span style={{ marginRight: 8 }}>
                        <i className="fa fa-times-circle" style={{ color: '#ff394f', fontSize: '1.5em' }}></i>
                      </span>
                      <FormattedMessage id="disponibilidadImportar.errors.error_al_importar" defaultMessage="Error al importar el archivo" />
                    </>
                  ) : (
                    <FormattedMessage id="disponibilidadImportar.success.reporte_de_la_importacion" defaultMessage="Reporte de la Importación" />
                  )}
                </h4>
                <button type="button" className="close" onClick={handleImportReportClose} aria-label="Close"><span aria-hidden="true">&times;</span></button>
              </div>
              <div className="modal-body modal-grey modal-mh ovf-x-hidden">
                <DisponibilidadImportarReporte data={importReportData} callbackReportClose={handleImportReportClose} />
                {modalImportReport === 'filetype' && (
                  <div className="alert alert-danger mt-3">
                    {importReportData && importReportData[0] && importReportData[0].errores[0]}
                  </div>
                )}
              </div>
              <div className="modal-footer modal-grey">
                <button type="button" className="btn btn-default btn-fleet" onClick={handleImportReportClose}>Ok</button>
              </div>
            </div>
          </div>
        </div>
      )}

      <div className="card-content collpase show">
        <div className="card-body card-dashboard">
          <div className="container-fluid mb-1">
            <div className="row dt-icons">
              <div className="col">
                <div
                  className="btn btn-grey-blue box-shadow-2 btn-round btn-lg btn-dt-main round-icon"
                  onClick={() => setDropdownActive(!dropdownActive)}
                >
                  {dropdownActive ? <UpIcon /> : <ExpandMoreIcon />}
                </div>
              </div>
              <div className="col">
                {searchActive && (
                  <div className="btn-dt-main round-icon">
                    <div
                      className="text-center"
                      style={{
                        height: '25px',
                        borderRadius: '3px',
                        display: 'flex',
                        justifyContent: 'center',
                        alignItems: 'center',
                        paddingLeft: '8px',
                        backgroundColor: '#5AAC43',
                        color: 'white',
                      }}
                    >
                      <div className="text-center" style={{ width: '100%', fontSize: '12px', fontWeight: '500' }}>
                        {moviles.length} de {paginationCount} resultados de búsqueda
                      </div>
                      <div className="resultados-busqueda-notif">
                        <i className="las la-times la-xs cursor-pointer" onClick={handleFilterReset} />
                      </div>
                    </div>
                  </div>
                )}
              </div>
              <div className="col">
                <div className="dt-buttons btn-group">
                  <div className="dropdown">
                    <StyledTooltip
                      title={intl.formatMessage({
                        id: 'movilesGrid.tools_export.title_exportar_a_excel',
                        defaultMessage: 'Exportar a Excel',
                      })}
                      arrow
                      placement="top"
                    >
                      <button
                        className="btn buttons-collection"
                        type="button"
                        id="dropdownMenuExportActiva"
                        data-toggle="dropdown"
                        aria-haspopup="true"
                        aria-expanded="false"
                      >
                        <span>
                          <i className="ft-upload"></i>
                        </span>
                      </button>
                    </StyledTooltip>
                    <div className="dropdown-menu" aria-labelledby="dropdownMenuExportActiva">
                      <button
                        style={{ fontSize: '15px' }}
                        onClick={handleModalExportOpen}
                        className="btn btn-secondary buttons-collection"
                        type="button"
                      >
                        <span>
                          <FormattedMessage id="disponibilidadActiva.export_modal.button" defaultMessage="Exportar Disponibilidad Activa" />
                        </span>
                      </button>
                    </div>
                  </div>

                  {Security.hasPermission("IMPORTAR_RUTAS_HISTORICAS") && (
                    <div className="dropdown">
                      <StyledTooltip
                        title={intl.formatMessage({
                          id: 'movilesGrid.tools_import.title_importar_excel',
                          defaultMessage: 'Importar Excel',
                        })}
                        arrow
                        placement="top"
                      >
                        <button
                          className="btn buttons-collection"
                          type="button"
                          id="dropdownMenuImportActiva"
                          data-toggle="dropdown"
                          aria-haspopup="true"
                          aria-expanded="false"
                        >
                          <span>
                            <i className="ft-download"></i>
                          </span>
                        </button>
                      </StyledTooltip>
                      <div className="dropdown-menu" aria-labelledby="dropdownMenuImportActiva">
                        <button
                          style={{ fontSize: '15px' }}
                          onClick={handleModalImportOpen}
                          className="btn btn-secondary buttons-collection"
                          type="button"
                        >
                          <span>
                            <FormattedMessage id="disponibilidadActiva.import_modal.button" defaultMessage="Importar Rutas" />
                          </span>
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>

          <DisponibilidadActivaGridFilters
            dropdownActive={dropdownActive}
            loading={loading}
            onSearch={handleFilterSearch}
            onReset={handleFilterReset}
          />

          <UltimaActualizacion
            inicioProceso={ultimaActualizacion && ultimaActualizacion.inicio_proceso}
            finProceso={ultimaActualizacion && ultimaActualizacion.fin_proceso}
          />

          <TableContainer>
            <Table aria-label="disponibilidad activa table" className="table-hover">
              <TableHead>
                <TableRow>
                  <StyledTableCell align="left" onClick={() => requestSort("dominio")} style={{ cursor: "pointer" }}>
                    <FormattedMessage id="disponibilidadActiva.col_dominio" defaultMessage="Dominio" />
                    {sortIcon("dominio")}
                  </StyledTableCell>
                  <StyledTableCell />
                  <StyledTableCell align="left" onClick={() => requestSort("motivo")} style={{ cursor: "pointer" }}>
                    <FormattedMessage id="disponibilidadActiva.col_motivo" defaultMessage="Motivo" />
                    {sortIcon("motivo")}
                  </StyledTableCell>
                  <StyledTableCell align="left" onClick={() => requestSort("modelo")} style={{ cursor: "pointer" }}>
                    <FormattedMessage id="disponibilidadActiva.col_modelo" defaultMessage="Modelo" />
                    {sortIcon("modelo")}
                  </StyledTableCell>
                  <StyledTableCell align="left" onClick={() => requestSort("tipo")} style={{ cursor: "pointer" }}>
                    <FormattedMessage id="disponibilidadActiva.col_tipo" defaultMessage="Tipo" />
                    {sortIcon("tipo")}
                  </StyledTableCell>
                  <StyledTableCell align="left" onClick={() => requestSort("base")} style={{ cursor: "pointer" }}>
                    <FormattedMessage id="disponibilidadActiva.col_base" defaultMessage="Base" />
                    {sortIcon("base")}
                  </StyledTableCell>
                  <StyledTableCell align="left" onClick={() => requestSort("subRegion")} style={{ cursor: "pointer" }}>
                    <FormattedMessage id="disponibilidadActiva.col_subRegion" defaultMessage="Sub.Región" />
                    {sortIcon("subRegion")}
                  </StyledTableCell>
                  <StyledTableCell align="left" onClick={() => requestSort("region")} style={{ cursor: "pointer" }}>
                    <FormattedMessage id="disponibilidadActiva.col_region" defaultMessage="Región" />
                    {sortIcon("region")}
                  </StyledTableCell>
                  <StyledTableCell show={columnsEnabled.transportadora} align="left" onClick={() => requestSort("transportadora")} style={{ cursor: "pointer" }}>
                    <FormattedMessage id="disponibilidadActiva.col_transportadora" defaultMessage="Transportadora" />
                    {sortIcon("transportadora")}
                  </StyledTableCell>
                  <StyledTableCell show={columnsEnabled.rentadora} align="left" onClick={() => requestSort("rentadora")} style={{ cursor: "pointer" }}>
                    <FormattedMessage id="disponibilidadActiva.col_rentadora" defaultMessage="Rentadora" />
                    {sortIcon("rentadora")}
                  </StyledTableCell>
                  <StyledTableCell align="left" onClick={() => requestSort("centroCostos")} style={{ cursor: "pointer" }}>
                    <FormattedMessage id="disponibilidadActiva.col_centroCostos" defaultMessage="Centro de Costos" />
                    {sortIcon("centroCostos")}
                  </StyledTableCell>
                  <StyledTableCell align="left" onClick={() => requestSort("fechaInicio")} style={{ cursor: "pointer" }}>
                    <FormattedMessage id="disponibilidadActiva.col_fechaInicio" defaultMessage="Fecha de inicio de ociosidad" />
                    {sortIcon("fechaInicio")}
                  </StyledTableCell>
                </TableRow>
              </TableHead>

              <TableBody>
                {loading && (
                  <TableRow>
                    <StyledTableCell colSpan={12} align="center">
                      <CircularProgress />
                    </StyledTableCell>
                  </TableRow>
                )}
                {!loading && (
                  moviles.length > 0
                    ? moviles.map((movil) => (
                        <DisponibilidadActivaGridRow
                          key={`activaRow_${movil.id}`}
                          movil={movil}
                          comboMotivos={comboMotivos}
                          onActualizarMotivo={handleActualizarMotivo}
                          columnsEnabled={columnsEnabled}
                        />
                      ))
                    : (
                        <TableRow>
                          <StyledTableCell colSpan={12} align="center">
                            <FormattedMessage id="disponibilidadActiva.sin_resultados" defaultMessage="No se encontraron resultados..." />
                          </StyledTableCell>
                        </TableRow>
                      )
                )}
              </TableBody>
            </Table>
          </TableContainer>

          <TablePagination
            rowsPerPageOptions={[5, 10, 25, 50, 100]}
            component="div"
            count={paginationCount}
            rowsPerPage={pagination.perPage}
            page={pagination.page}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
            labelRowsPerPage="Mostrar"
            labelDisplayedRows={({ from, to, count }) => `Mostrando ${from} a ${to} de ${count}`}
          />
        </div>
      </div>
    </div>
  );
};

export default DisponibilidadActivaGrid;
